<div class="max-w-2xl mx-auto px-2 sm:px-0 py-6">
  <!-- Loading -->
  @if (loading$ | async) {
    <div class="flex items-center justify-center py-16 text-gray-500">
      <span class="text-sm">Loading...</span>
    </div>
  }

  <!-- Error -->
  @if (error$ | async; as err) {
    <div
      class="rounded-xl border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm"
    >
      {{ err }}
    </div>
  }

  <!-- Content -->
  @if (!(loading$ | async) && !(error$ | async)) {
    <!-- Page Header -->
    <div class="mb-5">
      <h1 class="text-lg font-bold text-gray-900">Student Submissions</h1>
      <p class="text-sm text-gray-500 mt-1">
        Track student progress, review submissions, and see how your class is
        performing on this assignment.
      </p>
    </div>

    <!-- Test Assignment Card -->
    @if (assignment$ | async; as assignment) {
      @if (assignment.shareCode) {
        <div
          class="flex items-center justify-between gap-3 px-4 py-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg"
        >
          <div class="flex items-center gap-2 min-w-0">
            <lucide-icon
              [img]="icons.FlaskConical"
              class="w-3.5 h-3.5 text-amber-500 shrink-0"
            ></lucide-icon>
            <span class="text-sm text-amber-700">
              Preview exactly what your students will see.
            </span>
          </div>
          <button
            type="button"
            (click)="onTestAssignment()"
            class="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-white border border-amber-300 hover:border-amber-400 hover:bg-amber-50 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
          >
            Test as Student
          </button>
        </div>
      }
    }

    <!-- Submissions list -->
    @let list = (submissions$ | async) ?? [];

    @if (list.length === 0) {
      <div
        class="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center"
      >
        <div
          class="bg-gray-50 rounded-full p-5 w-16 h-16 mx-auto mb-4 flex items-center justify-center"
        >
          <lucide-icon
            [img]="icons.GraduationCap"
            class="w-8 h-8 text-gray-300"
          ></lucide-icon>
        </div>
        <h2 class="text-base font-semibold text-gray-700 mb-1">
          No submissions yet
        </h2>
        <p class="text-sm text-gray-400 max-w-sm mx-auto mb-5">
          Share the link below with your students so they can start the
          assignment. Their submissions will appear here automatically.
        </p>

        @if (assignment$ | async; as assignment) {
          @if (assignment.shareCode) {
            <div
              class="flex items-center gap-1.5 px-3 py-2 bg-green-50 border border-green-100 rounded-lg max-w-sm mx-auto"
            >
              <lucide-icon
                [img]="icons.Link"
                class="w-3.5 h-3.5 text-green-500 shrink-0"
              ></lucide-icon>
              <span
                class="text-xs text-green-700 font-medium truncate flex-1 select-all text-left"
                [title]="getShareLink()"
              >
                {{ getShareLink() }}
              </span>
              <button
                type="button"
                (click)="copyShareLink()"
                class="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md bg-green-500 hover:bg-green-600 text-white transition-colors cursor-pointer"
                [title]="copied() ? 'Copied!' : 'Copy link'"
              >
                @if (copied()) {
                  <lucide-icon
                    [img]="icons.Check"
                    class="w-3.5 h-3.5"
                  ></lucide-icon>
                } @else {
                  <lucide-icon
                    [img]="icons.Copy"
                    class="w-3.5 h-3.5"
                  ></lucide-icon>
                }
              </button>
            </div>
          }
        }
      </div>
    }

    @if (list.length > 0) {
      <!-- Grade Distribution -->
      @let distribution = getGradeDistribution(list);
      @let avgGrade = getAverageGrade(list);
      @if (distribution.length > 0) {
        <div
          class="bg-white rounded-xl border border-gray-100 px-4 py-3 mb-4"
        >
          <app-grade-distribution-chart
            [distribution]="distribution"
            [averageGrade]="avgGrade"
            [averageGradeColorClass]="
              avgGrade ? getAverageGradeColorClass(avgGrade) : ''
            "
          />
        </div>
      }

      <!-- Students count -->
      <p class="text-sm font-medium text-gray-500 mb-3 px-1">
        {{ list.length }}
        {{ list.length === 1 ? 'student' : 'students' }}
      </p>

      <!-- Student cards -->
      <div class="space-y-3">
        @for (submission of list; track submission.id) {
          <div
            class="bg-white rounded-xl border border-gray-200 hover:border-green-300 hover:shadow-md transition-all cursor-pointer group"
            (click)="viewSubmission(submission)"
          >
            <div class="px-3 py-2">
              <div class="flex items-center gap-2.5">
                <!-- Grade Badge -->
                <div
                  class="shrink-0 w-7 h-7 rounded-md flex items-center justify-center font-bold text-xs"
                  [ngClass]="getGradeBadgeClasses(submission)"
                >
                  @if (submission.status === 'in_progress') {
                    <lucide-icon
                      [img]="icons.Hourglass"
                      class="w-3.5 h-3.5"
                    ></lucide-icon>
                  } @else {
                    {{ getLetterGrade(submission) }}
                  }
                </div>

                <!-- Name + subtitle -->
                <div class="flex-1 min-w-0">
                  <h4
                    class="font-semibold text-gray-900 text-sm truncate"
                    [title]="getDisplayName(submission)"
                  >
                    {{ getDisplayName(submission) }}
                  </h4>
                  <p class="text-xs text-gray-400 truncate">
                    @if (submission.submittedAt) {
                      Finished
                      {{ formatAbsoluteTime(submission.submittedAt) }}
                      @if (getPointsLabel(submission); as points) {
                        <span class="mx-1">·</span>
                        <span class="text-gray-500 font-medium">
                          {{ points }}
                        </span>
                      }
                      @if (submission.gradePercentage !== null) {
                        <span class="text-gray-400">(<span class="font-medium" [ngClass]="getGradePercentageClass(submission)">{{ submission.gradePercentage }}%</span>)</span>
                      }
                    } @else {
                      In progress
                    }
                  </p>
                </div>

                <!-- View button -->
                <button
                  type="button"
                  class="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-white bg-green-500 hover:bg-green-600 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                  (click)="
                    $event.stopPropagation(); viewSubmission(submission)
                  "
                >
                  View
                  <lucide-icon
                    [img]="icons.ArrowRight"
                    class="w-3.5 h-3.5"
                  ></lucide-icon>
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    }
  }
</div>
